import { collection, getDocs, query, where, limit, onSnapshot, orderBy, serverTimestamp, doc, setDoc, updateDoc, deleteDoc, endBefore, limitToLast, getDoc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase';

const MAX_MESSAGE_LENGTH = 2000;

export function getOtherParticipantId(conv, meUid = auth.currentUser?.uid) {
  const me = meUid;
  const parts = Array.isArray(conv?.participants) ? conv.participants : [];
  return parts.find((p) => p !== me) || null;
}

export function getOtherSummary(conv, meUid = auth.currentUser?.uid) {
  const otherId = getOtherParticipantId(conv, meUid);
  const map = (conv?.participantSummaries) || {};
  const s = otherId ? (map[otherId] || {}) : {};
  return { id: otherId, displayName: s.displayName || '', photoURL: s.photoURL || '' };
}

export function formatRelativeTime(ts) {
  try {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff/60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff/3_600_000)}h ago`;
    return d.toLocaleDateString();
  } catch (err) {
    console.error('formatRelativeTime failed', err);
    return '';
  }
}

export async function blockUser(otherUid) {
  const me = auth.currentUser?.uid;
  if (!me || !otherUid || me === otherUid) return;
  try {
    const callable = httpsCallable(functions, 'blockUser');
    const resp = await callable({ otherUid });
    if (!resp?.data?.ok) {
      throw new Error('block-failed');
    }
  } catch (err) {
    console.error('blockUser callable failed', err);
    throw err instanceof Error ? err : new Error('block-failed');
  }
}

export async function unblockUser(otherUid) {
  const me = auth.currentUser?.uid;
  if (!me || !otherUid) return;
  try {
    const callable = httpsCallable(functions, 'unblockUser');
    const resp = await callable({ otherUid });
    if (!resp?.data?.ok) {
      throw new Error('unblock-failed');
    }
  } catch (err) {
    console.error('unblockUser callable failed', err);
    throw err instanceof Error ? err : new Error('unblock-failed');
  }
}

export async function hideConversation(conversationId) {
  const me = auth.currentUser?.uid;
  if (!me || !conversationId) return;
  // P2-CLI-6: Remove dangerous fallback to client write — just throw on failure
  const callable = httpsCallable(functions, 'hideConversation');
  await callable({ conversationId });
}

export async function startConversation(otherUid, firstMessageText = '') {
  const me = auth.currentUser?.uid;
  if (!me || !otherUid || me === otherUid) return null;
  const callable = httpsCallable(functions, 'startConversation');
  const res = await callable({ otherUid, firstMessageText });
  const cid = (res.data || {}).conversationId || null;
  return cid;
}

export function subscribeConversationsForUid(meUid, cb) {
  const me = meUid;
  if (!me) return () => {};
  const q = query(collection(db, 'conversations'), where('participants', 'array-contains', me), orderBy('lastMessageAt', 'desc'), limit(50));
  return onSnapshot(q, (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

// Backwards-compatible wrapper (uses auth.currentUser at call time).
export function subscribeConversations(cb) {
  return subscribeConversationsForUid(auth.currentUser?.uid, cb);
}

export function subscribeMessages(conversationId, cb) {
  return onSnapshot(query(collection(db, 'conversations', conversationId, 'messages'), orderBy('timestamp', 'asc'), limitToLast(200)), (snap) => {
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function hasOlderMessages(conversationId, oldestTimestamp) {
  if (!conversationId || !oldestTimestamp) return false;
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('timestamp', 'asc'),
    endBefore(oldestTimestamp),
    limitToLast(1)
  );
  const snap = await getDocs(q);
  return snap.size > 0;
}

export async function fetchOlderMessages(conversationId, oldestTimestamp) {
  if (!conversationId || !oldestTimestamp) return [];
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('timestamp', 'asc'),
    endBefore(oldestTimestamp),
    limitToLast(200)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function sendMessage(conversationId, text) {
  const me = auth.currentUser?.uid;
  const normalized = String(text || '').trim();
  if (!me || !normalized || !conversationId) return;
  if (normalized.length > MAX_MESSAGE_LENGTH) throw new Error('message-too-long');
  const convSnap = await getDoc(doc(db, 'conversations', conversationId));
  if (!convSnap.exists()) throw new Error('conversation-missing');
  const conv = convSnap.data() || {};
  const participants = Array.isArray(conv.participants) ? conv.participants : [];
  const other = participants.find((p) => p && p !== me) || null;
  if (!other) throw new Error('conversation-invalid');
  const allowNonFriend = conv.allowNonFriend === true || conv.context === 'swap_meet';
  const friendSnap = await getDoc(doc(db, 'users', me, 'friends', other));
  if (!friendSnap.exists() && !allowNonFriend) throw new Error('not-friends');
  const iBlockedSnap = await getDoc(doc(db, 'users', me, 'blocked', other));
  if (iBlockedSnap.exists()) throw new Error('you-blocked');
  const theyBlockedSnap = await getDoc(doc(db, 'users', other, 'blocked', me));
  if (theyBlockedSnap.exists()) throw new Error('they-blocked');
  // P1-CLI-3: Use batched write for atomic message + conversation update
  const batch = writeBatch(db);
  const msgRef = doc(collection(db, 'conversations', conversationId, 'messages'));
  batch.set(msgRef, {
    senderId: me,
    text: normalized.slice(0, MAX_MESSAGE_LENGTH),
    timestamp: serverTimestamp()
  });
  batch.set(doc(db, 'conversations', conversationId), {
    lastMessageText: normalized.slice(0, 80),
    lastMessageAt: serverTimestamp(),
    lastMessageSenderId: me
  }, { merge: true });
  await batch.commit();
}

export async function editMessage(conversationId, messageId, newText) {
  const me = auth.currentUser?.uid;
  if (!me || !newText) return;
  await updateDoc(doc(db, 'conversations', conversationId, 'messages', messageId), {
    text: String(newText).slice(0, 2000)
  });
}

export async function deleteMessage(conversationId, messageId) {
  const me = auth.currentUser?.uid;
  if (!me) return;
  await deleteDoc(doc(db, 'conversations', conversationId, 'messages', messageId));
}

export function subscribeMyParticipant(conversationId, cb) {
  const me = auth.currentUser?.uid;
  if (!me || !conversationId) return () => {};
  const ref = doc(db, 'conversations', conversationId, 'participants', me);
  return onSnapshot(ref, (snap) => cb(snap.exists() ? (snap.data() || {}) : {}));
}

export function subscribeMyParticipantsForUid(meUid, cb) {
  const me = meUid;
  if (!me) return () => {};
  const q = query(collection(db, 'conversations'), where('participants', 'array-contains', me), limit(100));
  const participantUnsubs = new Map();
  const participantMap = {};
  const stop = onSnapshot(q, (snap) => {
    const seen = new Set();
    snap.docChanges().forEach((change) => {
      const convId = change.doc.id;
      seen.add(convId);
      if (change.type === 'removed') {
        const unsub = participantUnsubs.get(convId);
        if (unsub) {
          try { unsub(); }
          catch (err) { console.error('subscribeMyParticipants cleanup failed', err); }
          participantUnsubs.delete(convId);
        }
        delete participantMap[convId];
        return;
      }
      if (!participantUnsubs.get(convId)) {
        const unsub = onSnapshot(doc(db, 'conversations', convId, 'participants', me), (psnap) => {
          participantMap[convId] = psnap.exists() ? ({ id: psnap.id, ...(psnap.data() || {}) }) : {};
          cb({ ...participantMap });
        }, (error) => {
          if (error?.code === 'permission-denied') {
            // Silence, but clear entry
            participantMap[convId] = {};
            cb({ ...participantMap });
          }
        });
        participantUnsubs.set(convId, unsub);
      }
    });
    // Clean up watchers for conversations not in the current snapshot
    Array.from(participantUnsubs.keys()).forEach((convId) => {
      if (!seen.has(convId) && !snap.docs.find((d) => d.id === convId)) {
        const unsub = participantUnsubs.get(convId);
        if (unsub) {
          try { unsub(); }
          catch (err) { console.error('subscribeMyParticipants cleanup failed', err); }
        }
        participantUnsubs.delete(convId);
        delete participantMap[convId];
      }
    });
  }, (error) => {
    if (error?.code === 'permission-denied') {
      cb({});
    }
  });
  return () => {
    try { stop(); } catch (err) { console.error('subscribeMyParticipants stop failed', err); }
    participantUnsubs.forEach((unsub) => {
      try { unsub(); }
      catch (err) { console.error('subscribeMyParticipants cleanup failed', err); }
    });
    participantUnsubs.clear();
  };
}

// Backwards-compatible wrapper (uses auth.currentUser at call time).
export function subscribeMyParticipants(cb) {
  return subscribeMyParticipantsForUid(auth.currentUser?.uid, cb);
}

export async function markConversationRead(conversationId) {
  const me = auth.currentUser?.uid;
  if (!me || !conversationId) return;
  await setDoc(doc(db, 'conversations', conversationId, 'participants', me), {
    uid: me,
    lastReadAt: serverTimestamp(),
    unreadCount: 0
  }, { merge: true });
} 
